/**
 * ProfileContext — single source of truth for profile state across the shell.
 *
 * Replaces the per-component localStorage polling pattern. Every component
 * that needs profile data consumes this context instead of calling
 * @yada/storage directly. The context refreshes on two events:
 *   - yada:profile:changed  — active profile switched
 *   - yada:profiles:updated — profile created / updated / deleted
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { Profile } from '@yada/types';
import { normalizeProfileSyncOrder } from '@yada/sync';
import { getCurrentProfile, getProfilesForCurrentAccount } from '../accounts/accountService';

export interface ProfileContextValue {
  /** The currently active profile, or null while loading. */
  activeProfile: Profile | null;
  /** All profiles belonging to the current account. */
  profiles: Profile[];
  /** True on first load, false once data has been fetched at least once. */
  isLoading: boolean;
  /** Re-fetch profiles from localStorage (called after any CRUD operation). */
  refreshProfiles: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

/**
 * Wrap the shell app tree with this provider (inside AccountProvider,
 * outside AppContent) so all components share one profile fetch.
 */
export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeProfile, setActiveProfileState] = useState<Profile | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refreshProfiles = useCallback(async () => {
    try {
      const [profile, allProfiles] = await Promise.all([
        getCurrentProfile(),
        getProfilesForCurrentAccount(),
      ]);
      const normalizedAll = normalizeProfileSyncOrder(allProfiles);
      const userFacingProfiles = normalizeProfileSyncOrder(normalizedAll.filter(p => p.profileType !== 'internal'));
      
      // If current profile is internal or not set, default to first user-facing profile, or first profile
      let effectiveActive = profile;
      if (!profile || (profile.profileType === 'internal' && userFacingProfiles.length > 0)) {
        effectiveActive = userFacingProfiles.find(p => p.profileType === 'standard') ||
                          userFacingProfiles.find(p => p.profileType !== 'household') ||
                          userFacingProfiles[0] || normalizedAll[0] || null;
      } else if (!effectiveActive && normalizedAll.length > 0) {
        effectiveActive = normalizedAll.find(p => p.profileType === 'standard') ||
                          normalizedAll.find(p => p.profileType !== 'household') ||
                          normalizedAll[0];
      }
      
      setActiveProfileState(effectiveActive);
      setProfiles(userFacingProfiles.length > 0 ? userFacingProfiles : normalizedAll);
    } catch (err) {
      console.error('[ProfileContext] Failed to load profiles:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshProfiles();

    // Refresh on switch (existing event) AND on CRUD (new event)
    const handler = () => void refreshProfiles();
    window.addEventListener('yada:profile:changed', handler);
    window.addEventListener('yada:profiles:updated', handler);
    return () => {
      window.removeEventListener('yada:profile:changed', handler);
      window.removeEventListener('yada:profiles:updated', handler);
    };
  }, [refreshProfiles]);

  return (
    <ProfileContext.Provider value={{ activeProfile, profiles, isLoading, refreshProfiles }}>
      {children}
    </ProfileContext.Provider>
  );
};

/**
 * Consume profile context. Must be used inside <ProfileProvider>.
 */
export function useProfileContext(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfileContext must be used within ProfileProvider');
  return ctx;
}
